import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, Params, Router, RouterStateSnapshot, UrlTree } from '@angular/router';
import { RoutingService } from '@spartacus/core';
import { Observable } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import {
  ArticleService,
  CatalogKeysService,
  CatalogRouterStateService,
  CategoryService,
  ProductService,
} from '../../../core/catalog';
import { TranslationService } from '../../../core/i18n';
import { CategoryRef } from '../../../core/model';
import { prepareUrlForLink, sanitizeArticleCode } from '../../../core/util';

@Injectable({ providedIn: 'root' })
export class RedirectGuard {
  constructor(
    private router: Router,
    private routingService: RoutingService,
    private articleService: ArticleService,
    private productService: ProductService,
    private categoryService: CategoryService,
    private catalogKeysService: CatalogKeysService,
    private translationService: TranslationService,
    private catalogRouterStateService: CatalogRouterStateService
  ) {}

  canActivate(route: ActivatedRouteSnapshot, routerState: RouterStateSnapshot): Observable<UrlTree | boolean> {
    // PDP - The URL contains query param "p" (p = productCode) OR params has "--p" and/or "a" (a = articleCode)
    const productCode = this.catalogRouterStateService.getProductCode(route.params, route.queryParams);
    if (!!productCode) {
      // IF "p" (p = productCode) and "a" (a = articleCode)
      const articleCode = this.hasParam(route.queryParams, 'a') ? sanitizeArticleCode(route.queryParams['a']) : undefined;
      if (!!articleCode) {
        return this.articleRedirect(articleCode, route);
      }

      // IF "p" (p = productCode) and not "a" (a = articleCode)
      return this.productRedirect(productCode, route);
    }

    // CATALOG - The URL contains query param "c" OR params has "--c"
    const categoryCode = this.catalogRouterStateService.getLeafCategoryCode(route.params, route.queryParams);
    if (!!categoryCode) {
      return this.categoryRedirect(categoryCode, route);
    }

    // CATALOG - Fallback no query params
    // Replacing "/c" with common.url.catalogKeyword_hint
    return this.fallbackCatalogRedirect(route, routerState);
  }

  private hasParam(queryParams: Params, param: string): boolean {
    return !!(queryParams && queryParams[param]);
  }

  private articleRedirect(articleCode: string, route: ActivatedRouteSnapshot): Observable<UrlTree | boolean> {
    return this.articleService.getArticle(articleCode).pipe(
      map((article) => {
        if (!article) {
          return this.handleNotFound();
        }

        return this.router.parseUrl(prepareUrlForLink(article.url, { ...route.queryParams }));
      })
    );
  }

  private productRedirect(productCode: string, route: ActivatedRouteSnapshot): Observable<UrlTree | boolean> {
    return this.catalogKeysService.getCatalogUsageKeyForParams(route.queryParams['mya']).pipe(
      switchMap((catalogUsageKey) => this.productService.getProduct(productCode, catalogUsageKey)),
      map((product) => {
        if (!product) {
          return this.handleNotFound();
        }

        return this.router.parseUrl(prepareUrlForLink(product.url, { ...route.queryParams }));
      })
    );
  }

  private categoryRedirect(categoryCode: CategoryRef, route: ActivatedRouteSnapshot): Observable<UrlTree | boolean> {
    return this.catalogKeysService.getCatalogUsageKeyForParams(route.queryParams['mya']).pipe(
      switchMap((catalogUsageKey) => this.categoryService.getCategory(categoryCode, catalogUsageKey)),
      map((category) => {
        if (!category) {
          return this.handleNotFound();
        }

        return this.router.parseUrl(prepareUrlForLink(category.url, { ...route.queryParams }));
      })
    );
  }

  private fallbackCatalogRedirect(route: ActivatedRouteSnapshot, routerState: RouterStateSnapshot): Observable<UrlTree> {
    return this.translationService.translate('common.url.catalogKeyword_hint').pipe(
      map((value) => {
        const url = routerState.url.replace('/c', `/${value || 'catalog'}`);
        return this.router.parseUrl(prepareUrlForLink(url, route.queryParams));
      })
    );
  }

  private handleNotFound(): boolean {
    this.routingService.go({ cxRoute: 'notFound' });
    return false;
  }
}
